import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-profile',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-5xl mx-auto">
        <div class="flex items-center justify-between gap-4 mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Profile</h1>
            <p class="text-slate-500 text-xs">Manage your account details and password.</p>
          </div>
          <span class="h-9 px-3 rounded-lg bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] text-xs font-bold inline-flex items-center gap-2">
            <i class="ti ti-shield-lock"></i> Secure Account
          </span>
        </div>

        @if (isLoading()) {
          <div class="bg-white rounded-xl border border-[#E7EEF5] p-10 text-center text-slate-500">
            <i class="ti ti-loader animate-spin text-3xl text-[#1A6FA6]"></i>
            <p class="text-sm font-semibold mt-3">Loading profile...</p>
          </div>
        } @else {
          <div class="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
            <aside class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-5 h-fit">
              <div class="w-16 h-16 rounded-xl text-white flex items-center justify-center text-xl font-bold shadow-sm" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                {{ initials() }}
              </div>
              <h2 class="text-lg font-semibold text-slate-950 mt-4">{{ profile.name || 'Admin User' }}</h2>
              <p class="text-sm font-semibold text-[#1A6FA6] mt-1">{{ profile.role || 'Administrator' }}</p>
              <div class="mt-4 space-y-2 text-xs text-slate-500">
                <p class="flex items-center gap-2"><i class="ti ti-mail text-slate-400"></i>{{ profile.email }}</p>
                <p class="flex items-center gap-2"><i class="ti ti-phone text-slate-400"></i>{{ profile.phone || 'No phone recorded' }}</p>
                <p class="flex items-center gap-2"><i class="ti ti-clock text-slate-400"></i>Last login {{ profile.last_login_at ? (profile.last_login_at | date:'d MMM y, h:mma') : 'not recorded' }}</p>
              </div>
            </aside>

            <main class="space-y-5">
              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                <div class="px-5 py-4 border-b border-slate-100">
                  <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Account Details</h2>
                  <p class="text-xs text-slate-500 mt-1">Your email and role are controlled by office administration.</p>
                </div>
                <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="block">
                    <span class="field-label">Full Name</span>
                    <input type="text" [(ngModel)]="profile.name" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Email</span>
                    <input type="email" [ngModel]="profile.email" disabled class="field-input disabled:opacity-70 disabled:cursor-not-allowed">
                  </label>
                  <label class="block">
                    <span class="field-label">Phone</span>
                    <input type="text" [(ngModel)]="profile.phone" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Role</span>
                    <input type="text" [ngModel]="profile.role" disabled class="field-input disabled:opacity-70 disabled:cursor-not-allowed">
                  </label>
                  <label class="block md:col-span-2">
                    <span class="field-label">Address</span>
                    <input type="text" [(ngModel)]="profile.address" class="field-input">
                  </label>
                  @if (profile.role === 'Admin') {
                    <label class="md:col-span-2 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" [(ngModel)]="profile.can_approve_leave" [ngModelOptions]="{standalone: true}" class="mt-0.5 rounded border-slate-300 text-[#1A6FA6]">
                      <span>
                        <span class="block text-xs font-bold text-slate-800">Can approve leave</span>
                        <span class="block text-[11px] leading-relaxed text-slate-500 mt-0.5">Receive new leave request emails and approve or decline them through the secure email link.</span>
                      </span>
                    </label>
                  }
                  <label class="block">
                    <span class="field-label">Emergency Contact Name</span>
                    <input type="text" [(ngModel)]="profile.emergency_contact_name" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Emergency Contact Phone</span>
                    <input type="text" [(ngModel)]="profile.emergency_contact_phone" class="field-input">
                  </label>
                </div>
                <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                  <button (click)="saveProfile()" [disabled]="isSavingProfile() || !profile.name?.trim()" class="h-9 px-4 rounded-lg bg-[#1A6FA6] text-white text-xs font-semibold hover:bg-[#0C3A5A] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2">
                    @if (isSavingProfile()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { <i class="ti ti-device-floppy"></i> Save Profile }
                  </button>
                </div>
              </section>

              <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
                <div class="px-5 py-4 border-b border-slate-100">
                  <h2 class="text-sm font-semibold text-slate-900 uppercase tracking-wider">Change Password</h2>
                  <p class="text-xs text-slate-500 mt-1">Use a new password that is different from your current one.</p>
                </div>
                <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label class="block">
                    <span class="field-label">Current Password</span>
                    <input type="password" [(ngModel)]="passwordForm.current_password" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">New Password</span>
                    <input type="password" [(ngModel)]="passwordForm.new_password" class="field-input">
                  </label>
                  <label class="block">
                    <span class="field-label">Confirm New Password</span>
                    <input type="password" [(ngModel)]="passwordForm.confirm_password" class="field-input">
                  </label>
                </div>
                <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                  <button (click)="changePassword()" [disabled]="isSavingPassword()" class="h-9 px-4 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2">
                    @if (isSavingPassword()) { <i class="ti ti-loader animate-spin"></i> Updating... } @else { <i class="ti ti-lock"></i> Update Password }
                  </button>
                </div>
              </section>
            </main>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .field-label {
      display: block;
      margin-bottom: .375rem;
      font-size: .75rem;
      font-weight: 700;
      color: #475569;
    }
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: .5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 .75rem;
      font-size: .875rem;
      color: #1e293b;
      outline: none;
    }
    .field-input:focus {
      border-color: #2B9AD2;
      background: #fff;
    }
  `]
})
export class AdminProfileComponent implements OnInit {
  private api = inject(ApiService);

  isLoading = signal(true);
  isSavingProfile = signal(false);
  isSavingPassword = signal(false);
  profile: any = {};
  passwordForm = {
    current_password: '',
    new_password: '',
    confirm_password: ''
  };

  ngOnInit() {
    this.loadProfile();
  }

  loadProfile() {
    this.isLoading.set(true);
    this.api.getProfile().subscribe({
      next: (res: any) => {
        this.profile = res.success ? (res.data?.profile || {}) : {};
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load profile.');
      }
    });
  }

  initials(): string {
    return String(this.profile?.name || 'AD').split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || 'AD';
  }

  saveProfile() {
    if (!this.profile.name?.trim()) {
      showToast('error', 'Name is required.');
      return;
    }
    this.isSavingProfile.set(true);
    this.api.updateProfile({
      name: this.profile.name,
      phone: this.profile.phone || '',
      address: this.profile.address || '',
      emergency_contact_name: this.profile.emergency_contact_name || '',
      emergency_contact_phone: this.profile.emergency_contact_phone || '',
      can_approve_leave: this.profile.can_approve_leave ? 1 : 0
    }).subscribe({
      next: (res: any) => {
        this.isSavingProfile.set(false);
        if (res.success) {
          showToast('success', 'Profile updated.');
          this.loadProfile();
        } else {
          showToast('error', res.message || 'Failed to update profile.');
        }
      },
      error: () => {
        this.isSavingProfile.set(false);
        showToast('error', 'Network error while updating profile.');
      }
    });
  }

  changePassword() {
    if (!this.passwordForm.current_password || !this.passwordForm.new_password) {
      showToast('error', 'Current and new passwords are required.');
      return;
    }
    if (this.passwordForm.new_password.length < 6) {
      showToast('error', 'New password must be at least 6 characters.');
      return;
    }
    if (this.passwordForm.new_password !== this.passwordForm.confirm_password) {
      showToast('error', 'New password and confirmation do not match.');
      return;
    }
    this.isSavingPassword.set(true);
    this.api.changePassword({
      current_password: this.passwordForm.current_password,
      new_password: this.passwordForm.new_password
    }).subscribe({
      next: (res: any) => {
        this.isSavingPassword.set(false);
        if (res.success) {
          showToast('success', 'Password updated.');
          this.passwordForm = { current_password: '', new_password: '', confirm_password: '' };
        } else {
          showToast('error', res.message || 'Failed to update password.');
        }
      },
      error: (err) => {
        this.isSavingPassword.set(false);
        showToast('error', err?.error?.message || 'Network error while updating password.');
      }
    });
  }
}
